import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import useTheme from '../../hooks/useTheme';
import useWebSocketConnection from '../../hooks/useWebSocketConnection';
import useCachedMarketData from './hooks/useCachedMarketData';
import usePriceSubscription from './hooks/usePriceSubscription';
import InstrumentCard from '../InstrumentCard/InstrumentCard';
import Pagination from './components/Pagination';
import { useWatchlist } from '../../contexts/WatchlistContext';
import './Markets.css';

const Markets = forwardRef(({ onMarketSelect }, ref) => {
  const { theme } = useTheme();
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [activeView, setActiveView] = useState('all'); // 'all' or 'watchlist'
  
  // Get connection status from hook
  const { connectionStatus, authStatus, socket } = useWebSocketConnection();

  // Get watchlist functionality
  const { watchlist, isLoading: watchlistLoading, fetchWatchlist } = useWatchlist();

  // Check if WebSocket is ready for API calls
  const isWebSocketReady = connectionStatus === 'connected' && authStatus === 'authenticated';

  // Get market data from hook (using cached version)
  const {
    instrumentCount,
    categories,
    loading,
    error,
    searchQuery,
    selectedCategory,
    currentPage,
    pageInstruments,
    filteredInstruments,
    totalPages,
    startIndex,
    endIndex,
    fetchData,
    fetchPageInstruments,
    handleCategoryChange,
    handleSearchChange,
    handlePageChange,
    setPageInstruments,
    clearCache // New from cached version
  } = useCachedMarketData(isWebSocketReady);

  // Auto-select first instrument when loading completes
  useEffect(() => {
    // Check if loading has finished and we have instruments
    if (!loading && filteredInstruments.length > 0 && !selectedInstrument) {
      // Select the first valid instrument
      const firstValidInstrument = filteredInstruments.find(
        instrument => !instrument.isPlaceholder
      );
      
      if (firstValidInstrument) {
        handleMarketSelect(firstValidInstrument);
      }
    }
  }, [loading, filteredInstruments, selectedInstrument]);

  // Get price subscription functionality from hook
  const {
    subscribeToPriceUpdates,
    subscriptionCount,
    lastUpdateTime,
    subscriptionUpdated,
    subscribedInstrumentsRef
  } = usePriceSubscription(isWebSocketReady, socket, pageInstruments, setPageInstruments);

  // Handle instrument selection
  const handleMarketSelect = (instrument) => {
    if (!instrument || instrument.isPlaceholder) return;
    
    // Update selected instrument state
    setSelectedInstrument(instrument);
    
    // Ensure we're passing the correct ID format to the parent
    const marketData = {
      ...instrument,
      // If originalId exists, use it for API calls; otherwise fallback to id
      id: instrument.originalId || instrument.id
    };
    
    // Call the parent's onMarketSelect function
    if (onMarketSelect) {
      onMarketSelect(marketData);
    }
  };

  // Custom handler for page changes that will also update price subscriptions
  const handlePageChangeWithSubscription = async (newPage) => {
    // Clear subscription reference to force resubscription
    subscribedInstrumentsRef.current = [];
    
    // Fetch instruments for the new page
    const instruments = await handlePageChange(newPage);
    
    // Subscribe to price updates for the new page of instruments
    if (instruments && instruments.length > 0) {
      subscribeToPriceUpdates(instruments);
    }
  };

  // Custom handler for category changes that will also update price subscriptions
  const handleCategoryChangeWithSubscription = async (category) => {
    // Clear subscription reference to force resubscription
    subscribedInstrumentsRef.current = [];
    
    // If in watchlist view, switch to all view when changing category
    if (activeView === 'watchlist') {
      setActiveView('all');
    }
    
    // Fetch instruments for the new category
    const instruments = await handleCategoryChange(category);
    
    // Subscribe to price updates for the new instruments
    if (instruments && instruments.length > 0) {
      subscribeToPriceUpdates(instruments);
    }
  };

  // Refresh markets data without fetching watchlist (parent component handles that)
  const refreshData = async () => {
    try {
      // Clear the cache before fetching new data
      clearCache();
      
      // Do not fetch watchlist here, Dashboard component handles that
      await fetchData();
      return true;
    } catch (error) {
      console.error('[Markets] Error refreshing data:', error);
      return false;
    }
  };

  // Get watchlist instruments with data from available market data
  const getWatchlistInstruments = () => {
    // Debug log the initial watchlist
    console.log('Raw watchlist:', watchlist);
    
    // Return empty array if watchlist is null/undefined or empty
    if (!watchlist || !Array.isArray(watchlist) || watchlist.length === 0) {
      return [];
    }
    
    // Filter out null entries before processing
    const validWatchlistItems = watchlist.filter(item => item !== null);
    
    // Map watchlist items to instruments with null checks
    const watchlistItems = validWatchlistItems.map(item => {
      // Debug log each item being processed
      console.log('Processing watchlist item:', item);
      
      // Skip invalid items
      if (!item || !item.instrumentId) {
        console.warn('Invalid watchlist item found:', item);
        return null;
      }

      // Try to find the matching instrument in our cached data
      const matchedInstrument = filteredInstruments.find(
        instr => instr && (instr.originalId || instr.id) === item.instrumentId
      );
      
      // Debug log the matching result
      console.log('Matched instrument for', item.instrumentId, ':', matchedInstrument);
      
      if (matchedInstrument) {
        return matchedInstrument;
      }
      
      // If not found, create a basic instrument from watchlist data
      const basicInstrument = {
        id: item.instrumentId,
        originalId: item.instrumentId,
        name: item.instrumentId.split('.').pop(),
        price: item.price || 0,
        bidPrice: item.bid || item.price || 0, // Use bid if available, fallback to price
        askPrice: item.ask || (item.price ? item.price * 1.001 : 0), // Use ask if available, or estimate
        change: item.change || 0,
        isPlaceholder: !item.price // Mark as placeholder if no price data
      };
      
      // Debug log the created basic instrument
      console.log('Created basic instrument:', basicInstrument);
      
      return basicInstrument;
    }).filter(Boolean); // Remove any remaining null items from the array
    
    // Debug log the final watchlist items
    console.log('Final watchlist items:', watchlistItems);
    
    return watchlistItems;
  };

  // Expose refresh method to parent components via ref
  useImperativeHandle(ref, () => ({
    refresh: refreshData
  }));

  // Subscribe to price updates whenever relevant instruments change
  useEffect(() => {
    if (isWebSocketReady) {
      refreshData();
    }
  }, [isWebSocketReady]);
  
  // Filter categories to remove any "Watchlist" category since we have a dedicated tab for it
  const filteredCategories = categories.filter(category => 
    category.id.toLowerCase() !== 'watchlist' && 
    category.name.toLowerCase() !== 'watchlist'
  );
  
  // Get the current instruments to display based on active view
  const displayInstruments = activeView === 'watchlist' 
    ? getWatchlistInstruments() 
    : filteredInstruments;
  
  // Debug log the display instruments when in watchlist view
  if (activeView === 'watchlist') {
    console.log('Watchlist count:', watchlist?.length);
    console.log('Display instruments count:', displayInstruments.length);
  }
  
  return (
    <div className={`markets-component ${theme}`}>
      {/* Header section with title and live updates */}
      <div className="markets-header">
        {/* Tabs for switching between All Markets and Watchlist */}
        <div className="markets-view-toggle">
          <button 
            className={`view-toggle-button markets-button ${activeView === 'all' ? 'active' : ''}`}
            onClick={() => setActiveView('all')}
            style={{ 
              color: activeView === 'all' ? (theme === 'light' ? '#000' : '#fff') : undefined,
              fontWeight: activeView === 'all' ? 'bold' : undefined
            }}
          >
            All Markets
          </button>
          <button 
            className={`view-toggle-button ${activeView === 'watchlist' ? 'active' : ''}`}
            onClick={() => setActiveView('watchlist')}
            style={{ 
              color: activeView === 'watchlist' ? (theme === 'light' ? '#000' : '#fff') : undefined,
              fontWeight: activeView === 'watchlist' ? 'bold' : undefined
            }}
          >
            My Watchlist
            {watchlist && watchlist.length > 0 && (
              <span className="watchlist-count">{watchlist.length}</span>
            )}
          </button>
        </div>
      </div>
      
      {/* Controls section with search and categories */}
      <div className="markets-controls">
        {activeView === 'all' && (
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="market-search"
          />
        )}
        
        <div className="markets-actions">
          {/* Category filters (All, Forex, etc) */}
          {activeView === 'all' && (
            <div className="market-categories">
              {filteredCategories.map(category => (
                <button
                  key={category.id}
                  className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategoryChangeWithSubscription(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
          
          {/* Live updates indicator */}
          <div className="live-updates-compact">
            <span className="updates-label">Live Updates:</span>
            <span className="updates-count">{subscriptionCount}</span>
            <span className="update-time">{lastUpdateTime ? lastUpdateTime.toLocaleTimeString() : ""}</span>
          </div>
        </div>
      </div>

      {/* Content area - keep this as the only scrollable container */}
      <div className="markets-container">
        {!isWebSocketReady ? (
          <div className="markets-loading">
            <div className="loading-spinner"></div>
            <p>Connecting...</p>
          </div>
        ) : loading && activeView === 'all' ? (
          <div className="markets-loading">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : watchlistLoading && activeView === 'watchlist' ? (
          <div className="markets-loading">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : error && activeView === 'all' ? (
          <div className="markets-error">
            <p>{error}</p>
            <button onClick={refreshData} className="retry-button">
              Retry
            </button>
          </div>
        ) : activeView === 'watchlist' && watchlist.length === 0 ? (
          <div className="empty-watchlist">
            <p>Your watchlist is empty</p>
            <button onClick={() => setActiveView('all')} className="view-all-button">
              View All Markets
            </button>
          </div>
        ) : displayInstruments.length > 0 ? (
          <>
            <div className="markets-grid">
              {displayInstruments.map((instrument) => (
                <InstrumentCard
                  key={instrument.id}
                  instrument={instrument}
                  onClick={handleMarketSelect}
                  isSelected={selectedInstrument && selectedInstrument.id === instrument.id}
                />
              ))}
            </div>
            
            {/* Show pagination only for all markets view with compact styling */}
            {activeView === 'all' && totalPages > 1 && 
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={handlePageChangeWithSubscription} 
              />
            }
          </>
        ) : searchQuery && activeView === 'all' ? (
          <div className="no-markets-found">
            <p>No markets found matching "{searchQuery}"</p>
          </div>
        ) : activeView === 'all' ? (
          <div className="no-markets-found">
            <p>No markets available. Try selecting a different category.</p>
            <button onClick={() => handleCategoryChangeWithSubscription('all')} className="retry-button">
              View All Markets
            </button>
          </div>
        ) : (
          <div className="no-markets-found">
            <p>No watchlist instruments could be loaded.</p>
            <button onClick={() => fetchWatchlist()} className="retry-button">
              Refresh Watchlist
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default Markets; 